import React, { useEffect, useRef, useState } from "react";
import { formatPrice } from "../lib/format";
import { Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1551803091-e20673f15770?w=900";
const CYCLE_MS = 1100; // ~1.1s per slide while hovered

export default function ProductCard({ product, index = 0 }) {
  const imgs = (product.images && product.images.length > 0) ? product.images : [FALLBACK_IMG];
  const primary = imgs[0];
  const stock = Object.values(product.stock_by_size || {}).reduce((a,b)=>a+b,0);
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const discountPct = onSale ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;

  const [showQuick, setShowQuick] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const intervalRef = useRef(null);
  const { add } = useCart();

  const startCycling = () => {
    setShowQuick(true);
    if (imgs.length < 2 || intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % imgs.length);
    }, CYCLE_MS);
  };

  const stopCycling = () => {
    setShowQuick(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setActiveIdx(0);
  };

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const quickAdd = (e, size) => {
    e.preventDefault(); e.stopPropagation();
    if ((product.stock_by_size || {})[size] <= 0) { toast.error(`Taille ${size} épuisée`); return; }
    add(product, size, 1);
    toast.success(`Ajouté ✿ ${product.name} · ${size}`);
  };

  return (
    <div className="group block animate-fade-up" style={{animationDelay: `${index*50}ms`}}
      onMouseEnter={startCycling}
      onMouseLeave={stopCycling}
      data-testid={`product-card-${product.id}`}>
      <Link to={`/produit/${product.id}`} className="block">
        <div className="relative overflow-hidden bg-berulia-beigewarm aspect-[3/4]">
          {imgs.map((src, i) => (
            <img
              key={`${src}-${i}`}
              src={src}
              alt={product.name}
              loading={i === 0 ? "eager" : "lazy"}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                i === activeIdx ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
          {/* Pagination dots when multiple images */}
          {imgs.length > 1 && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {imgs.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition ${
                    i === activeIdx ? "bg-berulia-ink" : "bg-white/70"
                  }`}
                />
              ))}
            </div>
          )}
          {product.is_new && <span className="absolute top-3 left-3 overline bg-berulia-beige text-berulia-ink px-2 py-1">Nouveau</span>}
          {onSale && <span className="absolute top-3 left-3 overline bg-berulia-rosedeep text-berulia-ink px-2 py-1">−{discountPct}%</span>}
          {product.is_favorite && !onSale && <span className="absolute top-3 right-3 overline bg-berulia-rose text-berulia-ink px-2 py-1">Coup de cœur</span>}
          {stock === 0 && <span className="absolute bottom-3 left-3 overline bg-berulia-rosedeep text-berulia-inkdeep px-2 py-1">Épuisé</span>}
          {/* Scarcity nudge on the listing tile: aggregates across sizes,
              fires when the product has only 1–2 units total left across
              every variant. Skipped when fully out of stock (Épuisé badge
              already covers it). */}
          {stock > 0 && stock <= 2 && (
            <span
              data-testid={`low-stock-${product.id}`}
              className="absolute bottom-3 left-3 overline bg-berulia-beige text-berulia-rosedeep px-2 py-1"
            >
              Plus que {stock} en stock
            </span>
          )}

          {/* Quick add overlay */}
          {stock > 0 && (
            <div className={`absolute bottom-0 left-0 right-0 bg-berulia-beige/95 backdrop-blur transition-all duration-300 ${showQuick ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
              <p className="overline text-center pt-3 text-berulia-inkmuted">Ajout rapide</p>
              <div className="flex flex-wrap justify-center gap-1.5 p-3">
                {(product.sizes || []).map(s => {
                  const av = (product.stock_by_size || {})[s] > 0;
                  return (
                    <button key={s} onClick={e => quickAdd(e, s)} disabled={!av}
                      data-testid={`quick-add-${product.id}-${s}`}
                      className={`min-w-[40px] px-2 py-1.5 overline text-[10px] border transition
                      ${av ? "bg-white border-berulia-rosedeep/60 hover:bg-berulia-rosedeep hover:text-berulia-inkdeep" : "border-berulia-border opacity-30 line-through cursor-not-allowed"}`}>{s}</button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Link>
      <Link to={`/produit/${product.id}`} className="block mt-4">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-heading text-xl leading-tight">{product.name}</p>
          <div className="text-right whitespace-nowrap">
            {onSale ? (
              <>
                <p className="font-heading text-lg text-berulia-rosedeep">{formatPrice(product.price)}</p>
                <p className="text-xs text-berulia-inkmuted line-through">{formatPrice(product.compare_at_price)}</p>
              </>
            ) : (
              <p className="font-heading text-lg">{formatPrice(product.price)}</p>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
